// Updates / Releases (Workstream D) — GitHub = artifact store; license server = authority.
//
// Shows: the configured Distribution Source, the SIGNED releases ingested from GitHub,
// the per-channel rollout target (set here), adoption (succeeded/failed per version),
// and what's currently deployed across the fleet (real telemetry). No synthetic data:
// a clean server with no source configured shows honest empty states.
import React, { useEffect, useState, useCallback } from 'react';
import { apiRequest } from '@/lib/supabase';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { UploadCloud, GitBranch, CheckCircle2, RefreshCw } from 'lucide-react';

interface Release {
  id: string; version: string; channel: string;
  image_digests: Record<string, string>; migrations: string[];
  min_from_version: string | null; rollback_safe: boolean;
  source_ref: string; ingested_at: string; manifest_sig: string;
}
interface ChannelTarget { channel: string; target_version: string; rollout: number; updated_at: string; }
interface Source { release_repo: string; channel_map: Record<string, string>; enabled: boolean; last_synced_at: string | null; }

const card: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 16 };
const muted: React.CSSProperties = { color: 'var(--text-muted)', fontSize: 13 };

export function UpdatesPage() {
  const [versions, setVersions] = useState<Array<{ version: string; count: number }>>([]);
  const [releases, setReleases] = useState<Release[]>([]);
  const [targets, setTargets] = useState<ChannelTarget[]>([]);
  const [adoption, setAdoption] = useState<Array<{ version: string; succeeded: number; failed: number }>>([]);
  const [source, setSource] = useState<Source | null>(null);
  const [repoInput, setRepoInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // "Set as target" rolls a channel to 100% of the fleet: it is confirmed first (DF-38).
  const [pendingTarget, setPendingTarget] = useState<Release | null>(null);

  const load = useCallback(() => {
    apiRequest<any>('/fleet').then((d) => setVersions(d?.version_distribution || [])).catch(() => setVersions([]));
    apiRequest<Release[]>('/distribution/releases').then(setReleases).catch(() => setReleases([]));
    apiRequest<ChannelTarget[]>('/distribution/channels').then(setTargets).catch(() => setTargets([]));
    apiRequest<any[]>('/distribution/adoption').then(setAdoption).catch(() => setAdoption([]));
    apiRequest<Source>('/distribution/source').then((s) => { setSource(s); if (s?.release_repo) setRepoInput(s.release_repo); }).catch(() => setSource(null));
  }, []);
  useEffect(load, [load]);

  const saveSource = async () => {
    if (!repoInput.trim()) return;
    setBusy(true); setMsg(null);
    try { await apiRequest('/distribution/source', { method: 'PUT', body: JSON.stringify({ release_repo: repoInput.trim() }) }); setMsg('Distribution source saved.'); load(); }
    catch (e: any) { setMsg(e.message); } finally { setBusy(false); }
  };
  const sync = async () => {
    setBusy(true); setMsg(null);
    try { const r = await apiRequest<any>('/distribution/sync', { method: 'POST' }); setMsg(`Synced: ${r.ingested} release(s) ingested + signed from ${r.source_repo}.`); load(); }
    catch (e: any) { setMsg(e.message); } finally { setBusy(false); }
  };
  const setTarget = async (channel: string, version: string) => {
    if (busy) return;
    setBusy(true); setMsg(null);
    try { await apiRequest(`/distribution/channels/${channel}`, { method: 'PUT', body: JSON.stringify({ target_version: version, rollout: 100 }) }); setMsg(`Channel ${channel} → ${version} (rollout 100%).`); load(); }
    catch (e: any) { setMsg(e.message); } finally { setBusy(false); setPendingTarget(null); }
  };

  const targetFor = (channel: string) => targets.find((t) => t.channel === channel)?.target_version;
  const adoptFor = (v: string) => adoption.find((a) => a.version === v);

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <UploadCloud size={20} /><h1 style={{ fontSize: 20, margin: 0 }}>Updates &amp; Releases</h1>
      </div>
      {msg && <div style={{ ...card, background: 'var(--ps-pill-blue-bg)', borderColor: 'var(--border)', fontSize: 13 }}>{msg}</div>}

      {/* Distribution Source */}
      <div style={card}>
        <h2 style={{ fontSize: 14, marginTop: 0, display: 'flex', alignItems: 'center', gap: 6 }}><GitBranch size={16} /> Distribution Source</h2>
        <p style={muted}>The release-only GitHub repo the server ingests Releases from. The GitHub App token is a server-side secret (env) and is never shown here. GitHub stores the bytes; the server signs the manifest on ingest.</p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={repoInput} onChange={(e) => setRepoInput(e.target.value)} placeholder="owner/polestead-releases"
            style={{ flex: 1, minWidth: 220, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13 }} />
          <button onClick={saveSource} disabled={busy} style={btn}>Save source</button>
          <button onClick={sync} disabled={busy || !source} style={btnPrimary}><RefreshCw size={14} style={{ verticalAlign: -2 }} /> Sync from GitHub</button>
        </div>
        {source?.last_synced_at && <div style={{ ...muted, marginTop: 6 }}>Last synced {new Date(source.last_synced_at).toLocaleString()}</div>}
      </div>

      {/* Ingested releases */}
      <div style={card}>
        <h2 style={{ fontSize: 14, marginTop: 0 }}>Ingested releases (signed on ingest)</h2>
        {releases.length === 0 ? <div style={muted}>No releases ingested yet. Configure a source and Sync from GitHub.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
              <th style={th}>Version</th><th style={th}>Channel</th><th style={th}>Migrations</th><th style={th}>Min from</th><th style={th}>Signed</th><th style={th}>Adoption</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {releases.map((r) => {
                const isTarget = targetFor(r.channel) === r.version; const a = adoptFor(r.version);
                return (
                  <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={td}><b>{r.version}</b></td>
                    <td style={td}>{r.channel}</td>
                    <td style={td}>{r.migrations?.length || 0}</td>
                    <td style={td}>{r.min_from_version || '—'}</td>
                    <td style={td}>{r.manifest_sig ? <CheckCircle2 size={14} color="var(--ps-pill-green-tx)" /> : '—'}</td>
                    <td style={td}>{a ? `${a.succeeded} ok / ${a.failed} fail` : '—'}</td>
                    <td style={td}>{isTarget ? <span style={{ color: 'var(--ps-pill-green-tx)', fontWeight: 600 }}>● current target</span> :
                      <button onClick={() => setPendingTarget(r)} disabled={busy} style={btn}>Set as target</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Channel targets */}
      <div style={card}>
        <h2 style={{ fontSize: 14, marginTop: 0 }}>Channel rollout targets</h2>
        {targets.length === 0 ? <div style={muted}>No channel target set. Installs stay on their current version until you set one (a new release is only <i>available</i>).</div> :
          targets.map((t) => <div key={t.channel} style={{ fontSize: 13 }}><b>{t.channel}</b> → {t.target_version} <span style={muted}>(rollout {t.rollout}%, updated {new Date(t.updated_at).toLocaleString()})</span></div>)}
      </div>

      {/* Currently deployed */}
      <div style={card}>
        <h2 style={{ fontSize: 14, marginTop: 0 }}>Currently deployed across the fleet</h2>
        {versions.length === 0 ? <div style={muted}>No installs have reported a version yet.</div> :
          versions.map((v) => <div key={v.version} style={{ fontSize: 13 }}><b>{v.version}</b> — {v.count} install{v.count !== 1 ? 's' : ''}</div>)}
      </div>

      <ConfirmDialog
        open={pendingTarget !== null}
        title={pendingTarget ? `Roll ${pendingTarget.channel} to ${pendingTarget.version}?` : ''}
        body={pendingTarget
          ? `Sets ${pendingTarget.version} as the ${pendingTarget.channel} channel target at 100% rollout: every install on ${pendingTarget.channel} is offered it on its next update check${targetFor(pendingTarget.channel) ? `, replacing ${targetFor(pendingTarget.channel)}` : ''}.${pendingTarget.migrations?.length ? ` It runs ${pendingTarget.migrations.length} migration${pendingTarget.migrations.length === 1 ? '' : 's'}.` : ''}${pendingTarget.rollback_safe ? '' : ' This release is not marked rollback-safe.'}`
          : ''}
        confirmLabel="Set as target"
        busy={busy}
        onConfirm={() => { if (pendingTarget) void setTarget(pendingTarget.channel, pendingTarget.version); }}
        onCancel={() => setPendingTarget(null)}
      />
    </div>
  );
}

const th: React.CSSProperties = { padding: '6px 8px', fontWeight: 600 };
const td: React.CSSProperties = { padding: '6px 8px' };
const btn: React.CSSProperties = { padding: '5px 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', fontSize: 12, cursor: 'pointer' };
const btnPrimary: React.CSSProperties = { ...btn, background: 'var(--accent)', color: 'white', borderColor: 'transparent' };
